import { useState, useRef } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Bot,
  MessageCircle,
  Clock,
  Target,
  TrendingUp,
  AlertCircle,
  CheckCircle2,
  ExternalLink,
  Loader2,
  Sparkles
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useSystemPrompts } from '@/hooks/use-system-prompts'
import { supabase } from '@/lib/supabase'

interface TestResult {
  answer: string
  matched_articles: Array<{
    id: number
    title: string
    similarity: number // Combined score (primary display)
    vector_score?: number // Semantic similarity
    text_score?: number // Fuzzy text match
    combined_score?: number // Weighted combination
    match_type?: string // 'fuzzy_match', 'exact_match', etc
    category?: string
    url: string
  }>
  metrics: {
    response_time_ms: number
    search_method: 'vector' | 'text_fallback' | 'hybrid'
    confidence: number | null
    top_similarity: number | null
  }
  vector_output?: string
  email_qa_review_id?: string
}

interface BernhardRequest {
  email_address: string
  email_name: string
  email_subject: string
  email_content: string
  systemprompt_categorization?: string
  systemprompt_emaildrafter?: string
  systemprompt_missionsolar?: string
  systemprompt_neoom?: string
  systemprompt_selfsolve?: string
  timestamp: string
}

// Helper: Extract JSON from potentially malformed string with multiple concatenated objects
const extractAllJSON = (text: string): any => {
  const jsonObjects = []
  let currentPos = 0

  while (currentPos < text.length) {
    const start = text.indexOf('{', currentPos)
    if (start === -1) break

    // Find matching closing brace with depth tracking
    let depth = 0
    let end = start

    for (let i = start; i < text.length; i++) {
      if (text[i] === '{') depth++
      if (text[i] === '}') {
        depth--
        if (depth === 0) {
          end = i + 1
          break
        }
      }
    }

    // Try to parse this JSON block
    try {
      const obj = JSON.parse(text.substring(start, end))
      jsonObjects.push(obj)
    } catch (e) {
      console.warn('Failed to parse JSON block at position', start)
    }

    currentPos = end
  }

  // Return based on what we found
  if (jsonObjects.length === 0) return null
  if (jsonObjects.length === 1) return jsonObjects[0]
  return jsonObjects // Multiple objects as array
}

export function AskAIBernhardWidget() {
  const [query, setQuery] = useState('')
  const [result, setResult] = useState<TestResult | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [history, setHistory] = useState<Array<{
    query: string
    timestamp: Date
    success: boolean
    answer?: string
    vector_output?: string
    email_qa_review_id?: string
  }>>([])
  const inputRef = useRef<HTMLInputElement>(null)
  const { getPrompt, loading: promptsLoading } = useSystemPrompts()

  // Load full answer from email_qa_reviews table
  const loadQaReview = async (qaId: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return null

      const { data, error } = await supabase
        .from('email_qa_reviews')
        .select('*')
        .eq('id', qaId)
        .single()

      if (error) throw error

      return data
    } catch (error) {
      console.error('Error loading QA review:', error)
      return null
    }
  }

  // Handle click on history item to load full response
  const handleHistoryItemClick = async (item: typeof history[0]) => {
    // Set query from history
    setQuery(item.query)

    // If we have stored answer, show it immediately
    if (item.answer) {
      setResult({
        answer: item.answer,
        matched_articles: [],
        metrics: {
          response_time_ms: 0,
          search_method: 'hybrid',
          confidence: 0.8,
          top_similarity: 0.8
        },
        vector_output: item.vector_output,
        email_qa_review_id: item.email_qa_review_id
      })
    }

    // If we have a QA review ID, load the full data
    if (item.email_qa_review_id) {
      setIsLoading(true)
      try {
        const qaData = await loadQaReview(item.email_qa_review_id)
        if (qaData) {
          setResult({
            answer: qaData.html_response || qaData.response_text || 'Keine Antwort gefunden',
            matched_articles: qaData.matched_articles || [],
            metrics: {
              response_time_ms: qaData.response_time_ms || 0,
              search_method: qaData.search_method || 'hybrid',
              confidence: qaData.confidence || 0.8,
              top_similarity: qaData.top_similarity || 0.8
            },
            vector_output: qaData.vector_output,
            email_qa_review_id: item.email_qa_review_id
          })
        }
      } catch (error) {
        console.error('Error loading QA review:', error)
      } finally {
        setIsLoading(false)
      }
    }
  }

  const handleTest = async () => {
    if (!query.trim()) return

    setIsLoading(true)
    setResult(null)

    try {
      // Build request in Email-Drafter format with latest system prompts
      const randomFirstNames = ['Anna', 'Max', 'Lena', 'Tim', 'Sophie', 'Jonas', 'Maria', 'Felix', 'Emma', 'Lucas', 'Hannah', 'Ben', 'Mia', 'David', 'Laura', 'Elias', 'Clara', 'Noah', 'Sarah', 'Samuel']
      const randomName = randomFirstNames[Math.floor(Math.random() * randomFirstNames.length)]

      const request: BernhardRequest = {
        email_address: 'dashboard-test@mission-solar.de',
        email_name: randomName,
        email_subject: `Frage: ${query}`,
        email_content: query,
        systemprompt_categorization: getPrompt('question_extractor') || getPrompt('categorization'),
        systemprompt_emaildrafter: getPrompt('email_styler_bernhard') || getPrompt('emaildrafter'),
        systemprompt_missionsolar: getPrompt('mission_solar_refiner') || getPrompt('missionsolar'),
        systemprompt_neoom: getPrompt('neoom_refiner') || getPrompt('neoom'),
        systemprompt_selfsolve: getPrompt('self_solve_refiner') || getPrompt('selfsolve'),
        timestamp: new Date().toISOString()
      }

      const response = await fetch(import.meta.env.VITE_WEBHOOK_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const htmlResponse = await response.text()

      // After webhook call, get the latest email_qa_reviews entry
      // Wait a moment for the database to be updated
      await new Promise(resolve => setTimeout(resolve, 1000))

      const { data: qaReview, error: qaError } = await supabase
        .from('email_qa_reviews')
        .select('*')
        .eq('session_id', request.email_address)
        .order('created_at', { ascending: false })
        .limit(1)
        .single()

      if (qaError) {
        console.error('Error loading QA review:', qaError)
      }

      // Build TestResult from QA review data
      let testData: TestResult

      if (qaReview) {
        // Parse vector_output if it exists
        let vectorOutputJson = null
        let matchedArticles = []

        try {
          if (qaReview.vector_output) {
            // Use robust JSON extraction (handles multiple concatenated objects)
            vectorOutputJson = extractAllJSON(qaReview.vector_output)

            // Handle different response formats
            if (Array.isArray(vectorOutputJson)) {
              // Array of article objects (actual webhook format)
              matchedArticles = vectorOutputJson.map(article => ({
                id: article.id,
                title: article.question,
                // Webhook returns: combined_score, vector_score, text_score (NOT similarity)
                similarity: article.combined_score || article.vector_score || article.similarity || 0,
                vector_score: article.vector_score || 0,
                text_score: article.text_score || 0,
                combined_score: article.combined_score || 0,
                match_type: article.match_type || 'unknown',
                category: article.category,
                url: `/knowledge?id=${article.id}&view=detail`
              }))
            } else if (vectorOutputJson?.matched_articles && Array.isArray(vectorOutputJson.matched_articles)) {
              // Nested format: {matched_articles: [...]}
              matchedArticles = vectorOutputJson.matched_articles.map(article => ({
                id: article.id,
                title: article.question,
                similarity: article.combined_score || article.vector_score || article.similarity || 0,
                vector_score: article.vector_score || 0,
                text_score: article.text_score || 0,
                combined_score: article.combined_score || 0,
                match_type: article.match_type || 'unknown',
                category: article.category,
                url: `/knowledge?id=${article.id}&view=detail`
              }))
            } else if (vectorOutputJson?.id) {
              // Single article case
              matchedArticles = [{
                id: vectorOutputJson.id,
                title: vectorOutputJson.question,
                similarity: vectorOutputJson.combined_score || vectorOutputJson.vector_score || vectorOutputJson.similarity || 0,
                vector_score: vectorOutputJson.vector_score || 0,
                text_score: vectorOutputJson.text_score || 0,
                combined_score: vectorOutputJson.combined_score || 0,
                match_type: vectorOutputJson.match_type || 'unknown',
                category: vectorOutputJson.category,
                url: `/knowledge?id=${vectorOutputJson.id}&view=detail`
              }]
            }
          }
        } catch (parseError) {
          console.warn('Could not parse vector output - showing raw data:', parseError)
        }

        testData = {
          answer: qaReview.final_response || qaReview.styler_output || qaReview.refiner_output || htmlResponse,
          matched_articles: matchedArticles,
          metrics: {
            response_time_ms: 0, // Would need to track this separately
            search_method: 'vector',
            confidence: vectorOutputJson?.similarity || 0.8,
            top_similarity: matchedArticles.length > 0 ? matchedArticles[0].similarity : 0.8
          },
          vector_output: qaReview.vector_output,
          email_qa_review_id: qaReview.id
        }
      } else {
        // Fallback to HTML response if no QA review found
        testData = {
          answer: htmlResponse,
          matched_articles: [],
          metrics: {
            response_time_ms: 0,
            search_method: 'hybrid',
            confidence: 0.8,
            top_similarity: 0.8
          }
        }
      }

      setResult(testData)

      // Add to history with full data
      setHistory(prev => [
        {
          query,
          timestamp: new Date(),
          success: true,
          answer: testData.answer,
          vector_output: testData.vector_output,
          email_qa_review_id: testData.email_qa_review_id
        },
        ...prev.slice(0, 4) // Keep last 5 items
      ])

    } catch (error) {
      console.error('Webhook test failed:', error)
      setResult({
        answer: 'Entschuldigung, bei der Anfrage an den Webhook ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut.',
        matched_articles: [],
        metrics: {
          response_time_ms: 0,
          search_method: 'text_fallback',
          confidence: null,
          top_similarity: null
        }
      })

      // Add failed attempt to history
      setHistory(prev => [
        { query, timestamp: new Date(), success: false },
        ...prev.slice(0, 4)
      ])
    } finally {
      setIsLoading(false)
    }
  }

  const getConfidenceColor = (confidence: number | null) => {
    if (!confidence) return 'text-gray-500'
    if (confidence >= 0.9) return 'text-green-600'
    if (confidence >= 0.75) return 'text-yellow-600'
    return 'text-red-600'
  }

  const getConfidenceLabel = (confidence: number | null) => {
    if (!confidence) return 'Keine Übereinstimmung'
    if (confidence >= 0.9) return 'Sehr hoch'
    if (confidence >= 0.75) return 'Hoch'
    if (confidence >= 0.6) return 'Mittel'
    return 'Niedrig'
  }

  const getSearchMethodBadge = (method: string) => {
    const variants = {
      vector: { label: 'Vector Search', variant: 'default' as const },
      text_fallback: { label: 'Text-Suche', variant: 'secondary' as const },
      hybrid: { label: 'Hybrid Search', variant: 'outline' as const },
      error: { label: 'Fehler', variant: 'destructive' as const }
    }
    return variants[method as keyof typeof variants] || variants.text_fallback
  }

  return (
    <Card className="w-full h-full">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="relative">
              <Bot className="h-6 w-6 text-missionSolar-primaryTeal" />
              <div className="absolute -top-1 -right-1 h-3 w-3 bg-green-500 rounded-full border-2 border-background" />
            </div>
            <div>
              <CardTitle className="text-lg font-semibold">Frag den KI Bernhard</CardTitle>
              <p className="text-sm text-muted-foreground">Testen Sie den Email-Drafter Webhook</p>
            </div>
          </div>
          </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Input Section */}
        <div className="space-y-3">
          <div className="flex gap-2">
            <Input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Stellen Sie eine Frage wie ein Kunde..."
              className="flex-1"
              onKeyDown={(e) => e.key === 'Enter' && handleTest()}
              disabled={isLoading || promptsLoading}
            />
            <Button
              onClick={handleTest}
              disabled={!query.trim() || isLoading || promptsLoading}
              className="px-4"
            >
              {isLoading || promptsLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <MessageCircle className="h-4 w-4" />
              )}
            </Button>
          </div>

          {/* Quick Suggestions */}
          <div className="flex flex-wrap gap-1">
            {[
              "Wie reset ich die Anlage?",
              "App funktioniert nicht",
              "Keine Verbindung mit Wallbox",
              "PV-Anlage zeigt keine Leistung"
            ].map((suggestion) => (
              <Button
                key={suggestion}
                variant="ghost"
                size="sm"
                className="h-7 text-xs px-2 py-1"
                onClick={() => setQuery(suggestion)}
                disabled={isLoading || promptsLoading}
              >
                {suggestion}
              </Button>
            ))}
          </div>
        </div>

        {/* Recent History */}
        {history.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Clock className="h-3 w-3" />
              <span>Kürzliche Tests</span>
            </div>
            <div className="space-y-1">
              {history.map((item, index) => (
                <div
                  key={index}
                  className={cn(
                    "flex items-center justify-between p-2 rounded-md text-xs cursor-pointer transition-colors",
                    "hover:bg-muted/50"
                  )}
                  onClick={() => handleHistoryItemClick(item)}
                >
                  <span className="truncate flex-1 mr-2">{item.query}</span>
                  <div className="flex items-center gap-1">
                    {item.success ? (
                      <CheckCircle2 className="h-3 w-3 text-green-500" />
                    ) : (
                      <AlertCircle className="h-3 w-3 text-yellow-500" />
                    )}
                    {item.email_qa_review_id && (
                      <div className="w-2 h-2 bg-blue-500 rounded-full" title="QA Review Daten verfügbar" />
                    )}
                    {item.vector_output && (
                      <div className="w-2 h-2 bg-green-500 rounded-full" title="Vector Output verfügbar" />
                    )}
                    <span className="text-muted-foreground">
                      {new Date(item.timestamp).toLocaleTimeString('de-DE', {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <Separator />

        {/* Prompts Loading */}
        {promptsLoading && (
          <div className="flex items-center gap-2 p-3 bg-muted/30 rounded-lg">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-sm text-muted-foreground">System-Prompts werden geladen...</span>
          </div>
        )}

        {/* Results Section */}
        {isLoading && (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-sm text-muted-foreground">Anfrage an Webhook wird gesendet...</span>
            </div>
            <div className="space-y-3">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-4/5" />
              <Skeleton className="h-4 w-3/5" />
            </div>
          </div>
        )}

        {result && !isLoading && (
          <div className="space-y-4">
            {/* Metrics Bar */}
            <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Target className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <div className={cn("text-sm font-medium", getConfidenceColor(result.metrics.confidence))}>
                      {result.metrics.confidence ? `${Math.round(result.metrics.confidence * 100)}%` : 'N/A'}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {getConfidenceLabel(result.metrics.confidence)}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <div className="text-sm font-medium">
                      {(result.metrics.response_time_ms / 1000).toFixed(1)}s
                    </div>
                    <div className="text-xs text-muted-foreground">Antwortzeit</div>
                  </div>
                </div>
              </div>

              <Badge
                variant={getSearchMethodBadge(result.metrics.search_method).variant}
                className="text-xs"
              >
                {getSearchMethodBadge(result.metrics.search_method).label}
              </Badge>
            </div>

            {/* AI Response */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Bot className="h-4 w-4 text-missionSolar-primaryTeal" />
                <span className="text-sm font-medium">Antwort vom Email-Drafter Webhook</span>
              </div>
              <div className="p-3 bg-blue-50/50 dark:bg-blue-950/20 rounded-lg border border-blue-200/50 dark:border-blue-800/50">
                <div
                  className="text-xs leading-relaxed webhook-response max-h-96 overflow-y-auto"
                  dangerouslySetInnerHTML={{ __html: result.answer }}
                />
              </div>
            </div>

            {/* Matched Articles from Vector Search */}
            {result.matched_articles && result.matched_articles.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-missionSolar-primaryTeal" />
                    <span className="text-sm font-medium">Vector Search Results</span>
                    <Badge variant="secondary" className="text-xs">
                      {result.matched_articles.length} Artikel
                    </Badge>
                  </div>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Sparkles className="h-3 w-3" />
                    <span>Top Score: {Math.round((result.matched_articles[0]?.similarity || 0) * 100)}%</span>
                  </div>
                </div>
                <div className="space-y-2">
                  {result.matched_articles.map((article, index) => {
                    const score = article.similarity || 0;
                    const vectorScore = article.vector_score || 0;
                    const textScore = article.text_score || 0;
                    const scoreColor = score >= 0.6 ? 'text-green-600' : score >= 0.5 ? 'text-amber-600' : 'text-red-600';
                    const scoreBg = score >= 0.6 ? 'bg-green-100 dark:bg-green-900/30' : score >= 0.5 ? 'bg-amber-100 dark:bg-amber-900/30' : 'bg-red-100 dark:bg-red-900/30';

                    // Detect TRUE duplicates (same question + same category = likely same source/content split)
                    const isDuplicate = result.matched_articles.filter(a =>
                      a.title === article.title && a.category === article.category
                    ).length > 1;

                    return (
                      <div
                        key={`${article.id}-${index}`}
                        className={cn(
                          "group relative p-3 rounded-lg border transition-all duration-200",
                          isDuplicate
                            ? "bg-yellow-50/50 dark:bg-yellow-950/10 border-yellow-300 dark:border-yellow-700"
                            : "bg-background border-muted hover:border-missionSolar-primaryTeal/30 hover:shadow-sm"
                        )}
                      >
                        <div className="flex items-start gap-3">
                          {/* Combined Score Badge */}
                          <div className={cn("flex-shrink-0 px-2 py-1 rounded-md text-xs font-semibold", scoreBg, scoreColor)}>
                            {Math.round(score * 100)}%
                          </div>

                          {/* Content */}
                          <div className="flex-1 min-w-0 space-y-2">
                            <div className="flex items-start justify-between gap-2">
                              <h4 className="text-sm font-medium line-clamp-2 group-hover:text-missionSolar-primaryTeal transition-colors flex-1">
                                {article.title}
                              </h4>
                              <a
                                href={`/knowledge?id=${article.id}&view=detail`}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 px-2 flex-shrink-0"
                                >
                                  <ExternalLink className="h-3 w-3" />
                                </Button>
                              </a>
                            </div>

                            {/* Hybrid Search Breakdown */}
                            <div className="flex items-center gap-3 text-[10px]">
                              <div className="flex items-center gap-1">
                                <span className="text-muted-foreground">Vector:</span>
                                <span className="font-mono font-semibold">{Math.round(vectorScore * 100)}%</span>
                              </div>
                              <span className="text-muted-foreground">·</span>
                              <div className="flex items-center gap-1">
                                <span className="text-muted-foreground">Text:</span>
                                <span className="font-mono font-semibold">{Math.round(textScore * 100)}%</span>
                              </div>
                              <span className="text-muted-foreground">·</span>
                              <Badge variant="outline" className="text-[9px] h-4 px-1">
                                {article.match_type}
                              </Badge>
                              {isDuplicate && (
                                <>
                                  <span className="text-muted-foreground">·</span>
                                  <Badge variant="outline" className="text-[9px] h-4 px-1 bg-yellow-50 border-yellow-300 text-yellow-700">
                                    Duplikat?
                                  </Badge>
                                </>
                              )}
                            </div>

                            {/* Meta: ID + Category */}
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                              <span className="font-mono">ID: {article.id}</span>
                              {article.category && (
                                <>
                                  <span>·</span>
                                  <span className="truncate">{article.category}</span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Vector Output */}
            {result.vector_output && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Target className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">Vector Search Payload</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs">
                      JSON
                    </Badge>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 px-2 text-xs"
                      onClick={() => {
                        navigator.clipboard.writeText(result.vector_output);
                      }}
                    >
                      Copy
                    </Button>
                  </div>
                </div>

                <div className="p-4 bg-slate-50 dark:bg-slate-950/50 rounded-lg border border-slate-200/50 dark:border-slate-800/50">
                  <div className="space-y-3">
                    {/* Quick Stats */}
                    {(() => {
                      try {
                        const parsed = extractAllJSON(result.vector_output);
                        const articleCount = parsed.matched_articles ? parsed.matched_articles.length : (parsed.id ? 1 : 0);
                        const topScore = parsed.matched_articles && parsed.matched_articles.length > 0 ?
                          parsed.matched_articles[0].similarity || 0 : parsed.similarity || 0;

                        return (
                          <div className="grid grid-cols-3 gap-3 text-xs">
                            <div className="text-center p-2 bg-white dark:bg-slate-900 rounded border border-slate-200 dark:border-slate-700">
                              <div className="font-semibold text-slate-900 dark:text-slate-100">Articles</div>
                              <div className="text-slate-600 dark:text-slate-400">{articleCount}</div>
                            </div>
                            <div className="text-center p-2 bg-white dark:bg-slate-900 rounded border border-slate-200 dark:border-slate-700">
                              <div className="font-semibold text-slate-900 dark:text-slate-100">Top Score</div>
                              <div className="text-slate-600 dark:text-slate-400">{Math.round(topScore * 100)}%</div>
                            </div>
                            <div className="text-center p-2 bg-white dark:bg-slate-900 rounded border border-slate-200 dark:border-slate-700">
                              <div className="font-semibold text-slate-900 dark:text-slate-100">Method</div>
                              <div className="text-slate-600 dark:text-slate-400">Vector</div>
                            </div>
                          </div>
                        );
                      } catch (error) {
                        return null;
                      }
                    })()}

                    {/* Formatted JSON */}
                    <div className="max-h-64 overflow-y-auto">
                      <pre className="text-xs leading-relaxed font-mono text-slate-700 dark:text-slate-300 whitespace-pre-wrap break-words">
                        {(() => {
                          try {
                            const parsed = extractAllJSON(result.vector_output);

                            // Format the JSON with better structure
                            const formatted = JSON.stringify(parsed, null, 2);

                            // Add syntax highlighting colors
                            return formatted
                              .replace(/"([^"]+)":/g, '<span class="text-blue-600 dark:text-blue-400">"$1"</span>:')
                              .replace(/: (null)/g, ': <span class="text-gray-500 dark:text-gray-400">null</span>')
                              .replace(/: (true|false)/g, ': <span class="text-green-600 dark:text-green-400">$1</span>')
                              .replace(/: (\d+\.?\d*)/g, ': <span class="text-purple-600 dark:text-purple-400">$1</span>');
                          } catch (error) {
                            // If parsing fails, try to extract JSON from the string
                            const jsonMatch = result.vector_output.match(/\{[\s\S]*\}/);
                            if (jsonMatch) {
                              try {
                                const parsed = JSON.parse(jsonMatch[0]);
                                return JSON.stringify(parsed, null, 2);
                              } catch {
                                return result.vector_output;
                              }
                            }
                            return result.vector_output;
                          }
                        })()}
                      </pre>
                    </div>
                  </div>
                </div>
              </div>
            )}

            </div>
        )}

        {/* Empty State */}
        {!result && !isLoading && (
          <div className="text-center py-8">
            <Bot className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <div className="space-y-2">
              <h3 className="text-lg font-medium">Bereit für Webhook-Test</h3>
              <p className="text-sm text-muted-foreground max-w-sm mx-auto">
                Stellen Sie eine Frage wie ein echter Kunde und testen Sie den Email-Drafter Webhook.
              </p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}